import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getChantier, createChantier, updateChantier } from "../services/api";
import Popup from "./Popup";
import "./ChantierForm.css";

// Helper function to format dates for API requests
const formatDateForAPI = (date, time = "00:00") => {
  if (!date) return null;
  // Ensure time is properly formatted
  const formattedTime = time || "00:00";
  // Create an ISO string and ensure it's in UTC
  return `${date}T${formattedTime}:00.000Z`;
};

const ChantierForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    numAttachement: "",
    numeroCommande: "", // NEW: optional field
    client: "",
    natureTravail: "",
    nomChantier: "",
    etat: "en cours",
    prixPrestation: "",
    adresseExecution: "",
    lieu: "",
    dateDebut: "",
    heureDebut: "",
    dateFin: "",
    heureFin: "",
    dateSaisie: new Date().toISOString().split("T")[0], // Add current date for dateSaisie
  });

  const [loading, setLoading] = useState(false);
  const [popup, setPopup] = useState({
    isOpen: false,
    title: "",
    content: null,
    type: "success",
  });
  const [createdChantierId, setCreatedChantierId] = useState(null); // add
  // NEW: field-level error for better UX on duplicates
  const [fieldError, setFieldError] = useState(null); // { field: 'numAttachement'|'numeroCommande', message: string }

  // Wrap fetchChantier in useCallback to prevent unnecessary re-renders
  const fetchChantier = useCallback(async () => {
    try {
      setLoading(true);
      const chantier = await getChantier(id);
      
      // Parse dateDebut and dateFin to extract date and time components
      let dateDebut = "";
      let heureDebut = "";
      let dateFin = "";
      let heureFin = "";
      
      if (chantier.dateDebut) {
        // Handle both ISO string and date objects
        const dateObj = new Date(chantier.dateDebut);
        if (!isNaN(dateObj.getTime())) {
          dateDebut = dateObj.toISOString().split("T")[0];
          heureDebut = dateObj.toTimeString().substring(0, 5);
        }
      }
      
      if (chantier.dateFin) {
        // Handle both ISO string and date objects
        const dateObj = new Date(chantier.dateFin);
        if (!isNaN(dateObj.getTime())) {
          dateFin = dateObj.toISOString().split("T")[0];
          heureFin = dateObj.toTimeString().substring(0, 5);
        }
      }
      
      setFormData({
        ...chantier,
        dateDebut,
        heureDebut,
        dateFin,
        heureFin,
        etat: chantier.etat || "en cours",
        dateSaisie: chantier.dateSaisie 
          ? new Date(chantier.dateSaisie).toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0],
      });
    } catch (error) {
      console.error("Error fetching chantier:", error);
      alert("Erreur lors du chargement du chantier");
      navigate("/");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (isEditing) {
      fetchChantier();
    }
  }, [id, isEditing, fetchChantier]);

  const handleChange = (e) => {
    // Clear field error on edit of the offending field
    if (fieldError && fieldError.field === e.target.name) {
      setFieldError(null);
    }
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setFieldError(null); // reset
      // Validate required fields (now includes both numbers)
      const requiredFields = ['numAttachement', 'numeroCommande', 'client', 'natureTravail', 'nomChantier'];
      const missingFields = requiredFields.filter(field => !formData[field] || formData[field].trim() === '');
      
      if (missingFields.length > 0) {
        throw new Error(`Les champs suivants sont obligatoires: ${missingFields.join(', ')}`);
      }
      
      // Prepare data for API (always non-null, trimmed)
      const chantierData = {
        numAttachement: formData.numAttachement.trim(),
        numeroCommande: formData.numeroCommande.trim(),
        client: formData.client.trim(),
        natureTravail: formData.natureTravail.trim(),
        nomChantier: formData.nomChantier.trim(),
        etat: formData.etat,
        prixPrestation: formData.prixPrestation ? parseFloat(formData.prixPrestation) : null,
        adresseExecution: formData.adresseExecution ? formData.adresseExecution.trim() : null,
        lieu: formData.lieu || null,
        dateDebut: formatDateForAPI(formData.dateDebut, formData.heureDebut),
        dateFin: formatDateForAPI(formData.dateFin, formData.heureFin),
        dateSaisie: formData.dateSaisie || new Date().toISOString().split('T')[0]
      };

      console.log('Sending data to server:', chantierData);

      if (isEditing) {
        await updateChantier(id, chantierData);
        setPopup({
          isOpen: true,
          title: "Chantier Modifié",
          content: (
            <div className="popup-chantier-details">
              <p>
                <strong>Le chantier a été modifié avec succès !</strong>
              </p>
              <div className="chantier-info">
                <p>
                  <span className="info-label">Nom du chantier:</span>{" "}
                  {formData.nomChantier}
                </p>
                <p>
                  <span className="info-label">N° bon de commande:</span>{" "}
                  {formData.numAttachement}
                </p>
                <p>
                  <span className="info-label">Client:</span> {formData.client}
                </p>
                <p>
                  <span className="info-label">Nature du travail:</span>{" "}
                  {formData.natureTravail}
                </p>
              </div>
            </div>
          ),
          type: "success",
        });
      } else {
        const response = await createChantier(chantierData);
        console.log('API response:', response);
        // Normalize axios vs fetch shape
        const resp = response && response.data ? response.data : response;
        
        // Store created chantier ID for redirect
        const newId = resp && (resp.id || resp._id);
        setCreatedChantierId(newId);
        console.log('Captured new chantier ID for redirect:', newId);

        // Check if auto-charge was created from server response
        const hasAutoCharge = !!(resp && resp.autoChargeCreated && Number(resp.autoChargeAmount) > 0);
        const autoChargeAmount = hasAutoCharge ? Number(resp.autoChargeAmount).toFixed(2) : 0;
        const autoChargeError = resp && resp.autoChargeError;

        console.log('Auto charge details:', { hasAutoCharge, autoChargeAmount, autoChargeError });

        setPopup({
          isOpen: true,
          title: "Chantier Ajouté",
          content: (
            <div className="popup-chantier-details">
              <p>
                <strong>
                  Chantier: {formData.nomChantier} ajouté avec succès !
                </strong>
              </p>
              <div className="chantier-info">
                <p>
                  <span className="info-label">N° bon de commande:</span>{" "}
                  {formData.numAttachement}
                </p>
                <p>
                  <span className="info-label">Client:</span> {formData.client}
                </p>
                <p>
                  <span className="info-label">Nature du travail:</span>{" "}
                  {formData.natureTravail}
                </p>
                <p>
                  <span className="info-label">Date début:</span>{" "}
                  {formData.dateDebut}
                </p>
                {formData.prixPrestation && (
                  <p>
                    <span className="info-label">Chiffre d'affaire:</span>{" "}
                    {parseFloat(formData.prixPrestation).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                  </p>
                )}
                {hasAutoCharge && (
                  <div style={{ marginTop: '16px', padding: '12px', background: '#e8f5e8', borderRadius: '4px', border: '1px solid #4caf50' }}>
                    <p style={{ margin: 0, fontWeight: 600, color: '#2e7d32' }}>
                      ✅ Charge automatique créée: {autoChargeAmount}€ (30% du chiffre d'affaire)
                    </p>
                    <p style={{ margin: '4px 0 0 0', fontSize: '0.9em', color: '#2e7d32' }}>
                      Cette charge sera visible dans la section "Gestion des charges" du chantier.
                    </p>
                  </div>
                )}
                {formData.prixPrestation && !hasAutoCharge && (
                  <div style={{ marginTop: '16px', padding: '12px', background: '#fff3cd', borderRadius: '4px', border: '1px solid #ffc107' }}>
                    <p style={{ margin: 0, fontWeight: 600, color: '#856404' }}>
                      ⚠️ Charge automatique non créée
                    </p>
                    <p style={{ margin: '4px 0 0 0', fontSize: '0.9em', color: '#856404' }}>
                      {autoChargeError ? `Erreur: ${autoChargeError}` : 'Vous pouvez ajouter manuellement une charge d\'acompte dans la section charges.'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ),
          type: "success",
        });
      }
    } catch (error) {
      console.error("Error saving chantier:", error);

      // Try to detect structured duplicate error from backend (HTTP 409)
      const status = error?.response?.status;
      const data = error?.response?.data || {};
      const rawMsg = String(
        data?.error || data?.message || error?.message || ""
      );

      const isDuplicate = status === 409 || /already exists/i.test(rawMsg);
      let dupField = null;
      let dupValue = "";
      if (isDuplicate) {
        dupField =
          data.field ||
          (/(numAttachement)/i.test(rawMsg) ? "numAttachement" :
           /(numeroCommande)/i.test(rawMsg) ? "numeroCommande" :
           null);
        dupValue =
          data.value ||
          ((rawMsg.match(/'(.+?)'/) || [])[1] || "");
      }

      if (isDuplicate && dupField) {
        // Set field-level error and focus the input
        const niceLabel = dupField === "numAttachement" ? "N° Bon de commande" : "Numéro de commande";
        const friendlyMsg = `${niceLabel} “${dupValue || (formData[dupField] || '')}” est déjà utilisé. Veuillez saisir un autre numéro.`;
        setFieldError({ field: dupField, message: friendlyMsg });
        setTimeout(() => {
          const el = document.querySelector(`input[name="${dupField}"]`);
          if (el) el.focus();
        }, 0);

        // Show well-styled popup matching your website styles
        setPopup({
          isOpen: true,
          title: "Conflit de numéro",
          type: "error",
          content: (
            <div
              style={{
                padding: "1rem",
                background: "#fff",
                borderRadius: "14px",
                border: "1px solid #f3d3d5",
                boxShadow: "0 6px 22px rgba(0,0,0,0.08)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: ".6rem",
                  marginBottom: ".75rem",
                }}
              >
                <span
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    background:
                      "linear-gradient(135deg, #ef4444 0%, #e11d48 100%)",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontWeight: 800,
                  }}
                >
                  !
                </span>
                <div style={{ fontWeight: 700, color: "#e11d48" }}>
                  {niceLabel} déjà utilisé
                </div>
              </div>
              <div style={{ color: "#444", lineHeight: 1.5, marginBottom: ".75rem" }}>
                Le {niceLabel.toLowerCase()} “
                <span style={{ fontWeight: 700, color: "#5b3d91" }}>
                  {dupValue || formData[dupField] || ""}
                </span>
                ” existe déjà pour un autre chantier.
              </div>
              <div style={{ color: "#6b7280", fontSize: ".92rem" }}>
                Saisissez un numéro différent pour continuer.
              </div>
            </div>
          ),
        });
        setLoading(false);
        return;
      }

      // Default error popup (fallback)
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Erreur lors de la sauvegarde. Vérifiez que tous les champs obligatoires sont remplis.";

      setPopup({
        isOpen: true,
        title: "Erreur",
        content: (
          <div className="popup-error">
            <p>{errorMessage}</p>
            {error.response?.status === 400 && (
              <p>
                <small>
                  Vérifiez que tous les champs obligatoires sont correctement renseignés.
                </small>
              </p>
            )}
          </div>
        ),
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const closePopup = () => {
    setPopup({ ...popup, isOpen: false });
    if (popup.type === "success") {
      // If we have a created chantier ID, go to its charges page
      if (!isEditing && createdChantierId) {
        console.log('Redirecting to charges page:', `/charges/${createdChantierId}`);
        navigate(`/charges/${createdChantierId}`);
      } else {
        navigate("/chantiers");
      }
    }
  };

  // Update the etat options to only include 'en cours' and 'fermé'
  const etatOptions = [
    { value: "en cours", label: "En cours" },
    { value: "fermé", label: "Fermé" },
    { value: "sav", label: "SAV" },
    // NEW
    { value: "annulé", label: "Annulé" }
  ];

  if (loading && isEditing) {
    return (
      <div className="form-page">
        <div className="form-container">
          <div className="form-header">
            <h2 className="form-title">Chargement...</h2>
          </div>
          <div className="form-content">
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div className="loading-spinner"></div>
              <p style={{ marginTop: '1rem', color: '#6b7280' }}>Chargement des données du chantier...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="form-page">
      <div className="form-container">
        <div className="form-header">
          <h2 className="form-title">
            🏗️ {isEditing ? "Modifier le Chantier" : "Nouveau Chantier"}
          </h2>
          <p className="form-subtitle">
            {isEditing 
              ? "Modifiez les informations de votre chantier"
              : "Créez un nouveau projet de construction"
            }
          </p>
          {isEditing && (
            <div className="status-badge editing">
              ✏️ Mode édition
            </div>
          )}
          
        </div>

        <div className="form-content">
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label required">
                  <span className="form-label-icon">📋</span>
                  N° Bon de commande
                </label>
                <div className="input-group">
                  <input
                    type="text"
                    name="numAttachement"
                    value={formData.numAttachement}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Ex: ATT-2024-001"
                    required
                    style={
                      fieldError?.field === "numAttachement"
                        ? {
                            borderColor: "#e11d48",
                            boxShadow: "0 0 0 3px rgba(225,29,72,.15)",
                          }
                        : undefined
                    }
                  />
                </div>
                {fieldError?.field === "numAttachement" && (
                  <div style={{ color: "#e11d48", fontSize: ".85rem", marginTop: 6 }}>
                    {fieldError.message}
                  </div>
                )}
                <p className="form-help-text">Obligatoire — identifiant Bon de commande (aussi appelé N° d’attachement)</p>
              </div>

              {/* Numéro de commande (now required) */}
              <div className="form-group">
                <label className="form-label required">
                  <span className="form-label-icon">🧾</span>
                  Numéro de commande
                </label>
                <div className="input-group">
                  <input
                    type="text"
                    name="numeroCommande"
                    value={formData.numeroCommande}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Ex: CMD-2024-045"
                    required
                    style={
                      fieldError?.field === "numeroCommande"
                        ? {
                            borderColor: "#e11d48",
                            boxShadow: "0 0 0 3px rgba(225,29,72,.15)",
                          }
                        : undefined
                    }
                  />
                </div>
                {fieldError?.field === "numeroCommande" && (
                  <div style={{ color: "#e11d48", fontSize: ".85rem", marginTop: 6 }}>
                    {fieldError.message}
                  </div>
                )}
                <p className="form-help-text">Obligatoire — identifiant de la commande</p>
              </div>

              <div className="form-group">
                <label className="form-label required">
                  <span className="form-label-icon">👤</span>
                  Client
                </label>
                <div className="input-group">
                  <input
                    type="text"
                    name="client"
                    value={formData.client}
                    onChange={handleChange}
                    className="form-input"
                    required
                    placeholder="Nom du client"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label required">
                  <span className="form-label-icon">🔧</span>
                  Nature du Travail
                </label>
                <div className="input-group">
                  <input
                    type="text"
                    name="natureTravail"
                    value={formData.natureTravail}
                    onChange={handleChange}
                    className="form-input"
                    required
                    placeholder="Type de travaux à effectuer"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label required">
                  <span className="form-label-icon">🏗️</span>
                  Nom du Chantier
                </label>
                <div className="input-group">
                  <input
                    type="text"
                    name="nomChantier"
                    value={formData.nomChantier}
                    onChange={handleChange}
                    className="form-input"
                    required
                    placeholder="Nom du projet"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    <span className="form-label-icon">📅</span>
                    Date début
                  </label>
                  <input
                    type="date"
                    name="dateDebut"
                    value={formData.dateDebut}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <span className="form-label-icon">🕐</span>
                    Heure début
                  </label>
                  <input
                    type="time"
                    name="heureDebut"
                    value={formData.heureDebut}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    <span className="form-label-icon">📅</span>
                    Date fin
                  </label>
                  <input
                    type="date"
                    name="dateFin"
                    value={formData.dateFin}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <span className="form-label-icon">🕐</span>
                    Heure fin
                  </label>
                  <input
                    type="time"
                    name="heureFin"
                    value={formData.heureFin}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <span className="form-label-icon">⚡</span>
                  État du chantier
                </label>
                <select
                  name="etat"
                  value={formData.etat}
                  onChange={handleChange}
                  className="form-input form-select"
                  disabled={!isEditing}
                >
                  {etatOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <span className="form-label-icon">💰</span>
                  Chiffre d'affaire (€)
                </label>
                <input
                  type="number"
                  name="prixPrestation"
                  value={formData.prixPrestation}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Ex: 10000"
                  min="0"
                  step="0.01"
                />
                <p className="form-help-text">Montant total prévu pour les travaux</p>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <span className="form-label-icon">📍</span>
                  <p className="form-label-help-text">Adresse d'exécution</p>
               
                </label>
                <input
                  type="text"
                  name="adresseExecution"
                  value={formData.adresseExecution}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Adresse complète du chantier"
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  <span className="form-label-icon">🏙️</span>
                  Lieu (Ville)
                </label>
                <input
                  type="text"
                  name="lieu"
                  value={formData.lieu}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Entrez le nom de la ville"
                />
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                onClick={() => navigate("/chantiers")}
                className="btn btn-secondary"
              >
                ❌ Annuler
              </button>
              <button type="submit" disabled={loading} className="btn btn-primary">
                {loading ? (
                  <>
                    <div className="loading-spinner"></div>
                    Sauvegarde...
                  </>
                ) : (
                  <>
                    {isEditing ? "✏️ Modifier" : "➕ Créer"}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      <Popup
        isOpen={popup.isOpen}
        onClose={closePopup}
        title={popup.title}
        type={popup.type}
      >
        {popup.content}
      </Popup>
    </div>
  );
};


export default ChantierForm;

